import RenderEntity, { RENDER_ENTITY_FORMAT3, RENDER_ENTITY_FORMAT4 } from 'ui-component/renders/RenderEntity';
import { lazy } from 'react';

// material-ui
import { Stack, Typography, Tooltip } from '@mui/material';

// third-party
import { format, parseJSON } from 'date-fns';
import { FormattedMessage } from 'react-intl';

// project imports
import { appState, appGridActions } from 'store/behaviors/loader';
import capabilities from './capabilities';
import UserSelect, { renderUser } from 'ui-component/fields/EntitySelect/admin/UserSelect';
import RenderDuration from 'ui-component/renders/RenderDuration';

// assets
import ContentCopyTwoToneIcon from '@mui/icons-material/ContentCopyTwoTone';
import AddLinkTwoToneIcon from '@mui/icons-material/AddLinkTwoTone';
import FileDownloadTwoToneIcon from '@mui/icons-material/FileDownloadTwoTone';
import ReceiptTwoToneIcon from '@mui/icons-material/ReceiptTwoTone';

// lazy loaded components
const ClientSelect = lazy(() => import('ui-component/fields/EntitySelect/crm/ClientSelect'));
const TicketTypeSelect = lazy(() => import('ui-component/fields/EntitySelect/workmgmt/TicketTypeSelect'));

const getTaskParent = (task) => task?.workOrder || task?.businessOpportunity || null;

const initialState = {
    ...appState(capabilities),
    colDef: [
        {
            field: 'actions',
            type: 'actions',
            description: '',
            align: 'center',
            hideable: false,
            sortable: false,
            resizable: false,
            disableReorder: true,
            label: 'ui-comp-table-actions',
            getActions: (params, permissionData, onClickActions) => {
                const gridActions = appGridActions(capabilities, params?.row, permissionData, onClickActions);

                permissionData?.forEach((perm) => {
                    switch (perm) {
                        case 'canCreateLink':
                            if (params?.row?.deletedAt || params?.row?.signature) {
                                return;
                            }

                            gridActions.push({
                                key: `actioncreatelink-row${params?.row?.id}`,
                                icon: <AddLinkTwoToneIcon fontSize="small" />,
                                label: 'ui-comp-button-createlink',
                                position: 2.1,
                                showInMenu: true,
                                onClick: () => {
                                    if (
                                        'onClickRowActionCreateLink' in (onClickActions || {}) &&
                                        typeof onClickActions.onClickRowActionCreateLink === 'function'
                                    ) {
                                        onClickActions.onClickRowActionCreateLink(params?.row);
                                    }
                                }
                            });
                            break;
                        case 'canCopyLink':
                            if (params?.row?.deletedAt || params?.row?.signature) {
                                return;
                            }

                            gridActions.push({
                                key: `actioncopylink-row${params?.row?.id}`,
                                icon: <ContentCopyTwoToneIcon fontSize="small" />,
                                label: 'ui-comp-button-copylink',
                                position: 2.2,
                                showInMenu: true,
                                onClick: () => {
                                    if (
                                        'onClickRowActionCopyLink' in (onClickActions || {}) &&
                                        typeof onClickActions.onClickRowActionCopyLink === 'function'
                                    ) {
                                        onClickActions.onClickRowActionCopyLink(params?.row);
                                    }
                                }
                            });
                            break;
                        case 'canExport':
                            if (params?.row?.deletedAt) {
                                return;
                            }

                            gridActions.push({
                                key: `actionexport-row${params?.row?.id}`,
                                icon: <FileDownloadTwoToneIcon fontSize="small" />,
                                label: 'ui-comp-button-export',
                                position: 2.3,
                                showInMenu: true,
                                onClick: () => {
                                    if (
                                        'onClickRowActionExportItem' in (onClickActions || {}) &&
                                        typeof onClickActions.onClickRowActionExportItem === 'function'
                                    ) {
                                        onClickActions.onClickRowActionExportItem(params?.row);
                                    }
                                }
                            });
                            break;
                        default: // do nothing
                    }
                });

                return gridActions;
            }
        },
        {
            field: 'id',
            type: 'number',
            description: '',
            align: 'left',
            hideable: false,
            sortable: false,
            resizable: true,
            label: 'traits-appentity-id'
        },
        {
            field: 'code',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={params.value}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <ReceiptTwoToneIcon sx={{ fontSize: '0.875rem', color: 'secondary' }} />
                            <Typography variant="inherit" color="inherit">
                                {params.value}
                            </Typography>
                        </Stack>
                    </Tooltip>
                ),

            label: 'traits-appentityhasautogencode-code'
        },
        {
            field: 'description',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <Tooltip
                    title={
                        <div
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: params?.value?.replace(/^((?:.|\n|\r){120})(?:.|\n|\r){2,}/, '$1…') }}
                        />
                    }
                >
                    <div
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: params?.value?.replace(/^((?:.|\n|\r){120})(?:.|\n|\r){2,}/, '$1…') }}
                    />
                </Tooltip>
            ),
            flex: 1,
            label: 'traits-appentityisdescribable-description'
        },
        {
            field: 'ticketType',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <RenderEntity
                    entityName="ticketType"
                    renderPattern="<name>"
                    renderTooltipPattern="[<acronym>] <name>"
                    renderFormat={RENDER_ENTITY_FORMAT3}
                    {...params}
                />
            ),
            label: 'entity-workmgmt-tickettype'
        },
        {
            field: 'client',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) =>
                params?.row?.client?.id ? (
                    <RenderEntity
                        entityName="client"
                        renderPattern="<name>"
                        renderTooltipPattern="[<code>] <name>"
                        renderFormat={RENDER_ENTITY_FORMAT4}
                        icon={params?.row?.client?.partnershipType?.icon}
                        color={params?.row?.client?.partnershipType?.color}
                        {...params}
                    />
                ) : (
                    <Tooltip
                        title={
                            <Typography color="inherit" variant="inherit">
                                {params?.row?.nonExistentClient} (<FormattedMessage id="entity-workmgmt-ticket-nonexistentclient" />)
                            </Typography>
                        }
                    >
                        <Typography color="inherit" variant="inherit">
                            {params?.row?.nonExistentClient} (<FormattedMessage id="entity-workmgmt-ticket-nonexistentclient" />)
                        </Typography>
                    </Tooltip>
                ),
            label: 'entity-crm-client'
        },
        {
            field: 'task',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            renderCell: (params) => {
                const task = params?.row?.task;
                const taskParent = getTaskParent(task);

                if (!task) {
                    return null;
                }

                const taskLabel = `[${task?.code || ''}] ${task?.name || ''}`;
                const parentLabel = taskParent ? `[${taskParent?.code || ''}] ${taskParent?.name || ''}` : '';

                return (
                    <Tooltip
                        title={
                            <Typography color="inherit" variant="inherit">
                                {[parentLabel, taskLabel].filter(Boolean).join(' / ')}
                            </Typography>
                        }
                    >
                        <Typography color="inherit" variant="inherit">
                            {[taskParent?.name, task?.name].filter(Boolean).join(' / ')}
                        </Typography>
                    </Tooltip>
                );
            },
            label: 'entity-workmgmt-workorder'
        },
        {
            field: 'signatory',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            label: 'entity-workmgmt-ticket-signatory'
        },
        {
            field: 'workDuration',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => params?.value && <RenderDuration {...params} />,
            label: 'entity-workmgmt-ticket-workduration'
        },
        {
            field: 'startDate',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'entity-workmgmt-ticket-startdate'
        },
        {
            field: 'endDate',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'entity-workmgmt-ticket-enddate'
        },
        {
            field: 'createdAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-createdat'
        },
        {
            field: 'createdBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-createdby'
        },
        {
            field: 'updatedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-updatedat'
        },
        {
            field: 'updatedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-updatedby'
        },
        {
            field: 'deletedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentityissafedeletable-deletedat'
        },
        {
            field: 'deletedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentityissafedeletable-deletedby'
        }
    ],
    dataGridState: {
        columns: {
            columnVisibilityModel: {
                id: false,
                code: true,
                description: true,
                ticketType: true,
                client: true,
                task: true,
                signatory: true,
                workDuration: true,
                startDate: false,
                endDate: false,
                createdAt: true,
                createdBy: true,
                updatedAt: false,
                updatedBy: false,
                deletedAt: false,
                deletedBy: false
            },
            orderedFields: [
                'actions',
                'id',
                'code',
                'description',
                'ticketType',
                'client',
                'task',
                'signatory',
                'workDuration',
                'startDate',
                'endDate',
                'createdAt',
                'createdBy',
                'updatedAt',
                'updatedBy',
                'deletedAt',
                'deletedBy'
            ],
            dimensions: {
                actions: { width: 60 },
                id: { width: 100 },
                code: { width: 100 },
                description: { width: 300 },
                ticketType: { width: 150 },
                client: { width: 150 },
                task: { width: 150 },
                signatory: { width: 150 },
                workDuration: { width: 100 },
                startDate: { width: 100 },
                endDate: { width: 100 },
                createdAt: { width: 100 },
                createdBy: { width: 100 },
                updatedAt: { width: 100 },
                updatedBy: { width: 100 },
                deletedAt: { width: 100 },
                deletedBy: { width: 100 }
            }
        },
        sorting: {
            sortModel: [{ field: 'id', sort: 'desc' }]
        },
        toolbarFilters: { includeSafeDeleted: false, pendingAssociation: false, pendingSignature: false }
    },
    filterDef: [
        {
            filterType: 'string',
            field: { fieldName: 'code', fieldType: 'simple' },
            label: 'traits-appentityhasautogencode-code'
        },
        {
            filterType: 'string',
            field: { fieldName: 'description', fieldType: 'simple' },
            label: 'traits-appentityisdescribable-description'
        },
        {
            filterType: 'string',
            field: { fieldName: 'ticketTypeId', fieldType: 'simple' },
            label: 'entity-workmgmt-tickettype',
            renderField: (params) => <TicketTypeSelect key="filterTicket-tickettype" multiple {...params} />
        },
        // TODO: Find a way to do the search on the client field, and have it generate a search configuration on the client and nonExistentClient fields simultaneously
        {
            filterType: 'string',
            field: { fieldName: 'nonExistentClient', fieldType: 'simple' },
            label: 'entity-workmgmt-ticket-nonexistentclient'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'clientId', fieldType: 'simple' },
            label: 'entity-crm-client',
            renderField: (params) => <ClientSelect key="filterTicket-client" multiple {...params} />
        },
        {
            filterType: 'string',
            field: { fieldName: 'signatory', fieldType: 'simple' },
            label: 'entity-workmgmt-ticket-signatory'
        },
        {
            filterType: 'number',
            field: { fieldName: 'workDuration', fieldType: 'simple' },
            label: 'entity-workmgmt-ticket-workduration'
        },
        {
            filterType: 'startDate',
            field: { fieldName: 'startDate', fieldType: 'simple' },
            label: 'entity-workmgmt-ticket-startdate'
        },
        {
            filterType: 'endDate',
            field: { fieldName: 'endDate', fieldType: 'simple' },
            label: 'entity-workmgmt-ticket-enddate'
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'createdAt', fieldType: 'simple' },
            label: 'traits-appentity-createdat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'createdBy', fieldType: 'simple', outputFieldName: 'createdById' },
            label: 'traits-appentity-createdby',
            renderField: (params) => <UserSelect key="filterTicket-createdBy" multiple displayMode="fullName" {...params} />
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'updatedAt', fieldType: 'simple' },
            label: 'traits-appentity-updatedat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'updatedBy', fieldType: 'simple', outputFieldName: 'updatedById' },
            label: 'traits-appentity-updatedby',
            renderField: (params) => <UserSelect key="filterTicket-updatedBy" multiple displayMode="fullName" {...params} />
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'deletedAt', fieldType: 'simple' },
            label: 'traits-appentityissafedeletable-deletedat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'deletedBy', fieldType: 'simple', outputFieldName: 'deletedById' },
            label: 'traits-appentityissafedeletable-deletedby',
            renderField: (params) => <UserSelect key="filterTicket-deletedBy" multiple displayMode="fullName" {...params} />
        }
    ],
    quickSearchFilterDef: {
        placeholder: 'ui-comp-quicksearchplaceholder-ticket',
        fields: [
            { fieldName: 'code', fieldType: 'simple' },
            { fieldName: 'description', fieldType: 'simple' },
            { fieldName: 'clientName', fieldType: 'simple' },
            { fieldName: 'nonExistentClient', fieldType: 'simple' }
        ]
    },
    sortingDef: [
        { fieldName: 'id', fieldType: 'simple' },
        { fieldName: 'code', fieldType: 'simple' },
        { fieldName: 'name', fieldType: 'simple' },
        { fieldName: 'createdAt', fieldType: 'simple' },
        { fieldName: 'createdBy', fieldType: 'simple' },
        { fieldName: 'updatedAt', fieldType: 'simple' },
        { fieldName: 'updatedBy', fieldType: 'simple' },
        { fieldName: 'deletedAt', fieldType: 'simple' },
        { fieldName: 'deletedBy', fieldType: 'simple' }
    ]
};

export default initialState;
